import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useSearchParams } from 'react-router-dom';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Mock data removed - will load from API when available

const FindHelpChart = () => {
  const [chartData, setChartData] = useState(null);
  const [gridData, setGridData] = useState([]);
  const [showGrid, setShowGrid] = useState(false);
  const [showExportButton, setShowExportButton] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const chartRef = useRef();

  const memberId = searchParams.get('memberID') || 'DEMO123';

  useEffect(() => {
    const loadChartData = async () => {
      try {
        setLoading(true);
        
        // API call to load chart data will be implemented here
        // For now, showing empty state
        setChartData(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (memberId) {
      loadChartData();
    }
  }, [memberId]);

  const handleChartClick = async (event, elements) => {
    if (elements.length === 0) {
      setShowGrid(false);
      setGridData([]);
      setShowExportButton(false);
      return;
    }

    const element = elements[0];
    const labelIndex = element.index;
    const datasetIndex = element.datasetIndex;
    
    // API call will be implemented here to get label and range from actual data
    // const label = chartData.labels[labelIndex];
    // const range = chartData.datasets[datasetIndex].label;
    
    try {
      // API call will be implemented here to fetch detailed referral data
      // For now, showing empty state
      setShowGrid(false);
      setGridData([]);
      setShowExportButton(false);
    } catch (err) {
      console.error('Error fetching grid data:', err);
      setShowGrid(false);
      setGridData([]);
      setShowExportButton(false);
    }
  };

  const handleExportClick = () => {
    if (gridData.length === 0) {
      alert("No records to download");
      return;
    }

    // Create CSV content
    const headers = [
      'CCA referral ID', 'Member Name', 'Member ID', 'Medicare', 'Medicaid',
      'Staff Name', 'Referral Date', 'Referral Status', 'Referral LastUpdated',
      'Provider Name', 'Program Name', 'Category', 'Days SinceReferral'
    ];
    
    const csvContent = [
      headers.join(','),
      ...gridData.map(row => [
        row.ccaReferralId,
        row.memberName,
        row.memberId,
        row.medicare,
        row.medicaid,
        row.staffName,
        formatDate(row.referralDate),
        row.referralStatus,
        formatDate(row.referralLastUpdated),
        row.providerName,
        row.programName,
        row.category,
        row.daysSinceReferral
      ].map(field => `"${field}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'FindHelpChart.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '0001-01-01T00:00:00') return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getLabelColor = (index) => {
    // Replicate the color logic from the original chart
    const colors = [
      'green',    // Closed Loop - Got Help
      'orange',   // Housing
      'orange',   // Food
      'orange',   // Transportation
      'orange',   // Utilities
      'red',      // Medical Care
      'red',      // Mental Health
      'red',      // Substance Abuse
      'red',      // Legal Services
      'red',      // Employment
      'red'       // Education
    ];
    return colors[index] || 'black';
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    },
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'FindHelp-Member Chart'
      },
      datalabels: {
        color: 'black',
        font: {
          weight: 'bold'
        },
        align: 'center',
        anchor: 'center',
        formatter: (value) => value > 0 ? value : ''
      },
      tooltip: {
        callbacks: {
          title: (context) => {
            const label = context[0].label;
            return Array.isArray(label) ? label.join(' ') : label;
          }
        }
      }
    },
    scales: {
      x: {
        maxBarThickness: 60,
        stacked: true,
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          color: function(context) {
            return getLabelColor(context.index);
          }
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  if (!memberId) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <div id="no-data">No data to display.</div>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center">Loading chart data...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">Error loading chart: {error}</div>;
  }

  if (!chartData) {
    return <div className="alert alert-info">No data available for chart.</div>;
  }

  return (
    <div className="findhelp-chart">
      {/* Chart Container */}
      <div className="chart-container" style={{ height: '100vh', display: 'flex' }}>
        <Bar ref={chartRef} data={chartData} options={options} />
      </div>

      {/* Export Button */}
      {showExportButton && (
        <div style={{
          marginTop: '26px',
          bottom: '-48px',
          position: 'relative',
          marginLeft: '43px'
        }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleExportClick}
            style={{
              float: 'right',
              position: 'relative',
              top: '-41px',
              marginRight: '37px',
              fontSize: '11px',
              color: 'white',
              fontWeight: 'bold',
              backgroundColor: '#428BCA'
            }}
          >
            Export Report
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="container" style={{
        border: '1px solid black',
        padding: '4px',
        margin: '10px auto',
        width: '60%'
      }}>
        <div className="row" style={{ display: 'flex' }}>
          <div className="col-sm-4" style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              backgroundColor: '#4eaa4e',
              width: '12px',
              height: '12px',
              display: 'inline-block',
              margin: '2px 2px'
            }}></div>
            <div style={{ display: 'inline-block', fontSize: '12px' }}>
              Closed Loop - Got Help Status
            </div>
          </div>
          <div className="col-sm-4" style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              backgroundColor: '#eab00a',
              width: '12px',
              height: '12px',
              display: 'inline-block',
              margin: '2px 2px'
            }}></div>
            <div style={{ display: 'inline-block', fontSize: '12px' }}>
              In Progress Status
            </div>
          </div>
          <div className="col-sm-4" style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              backgroundColor: 'red',
              width: '12px',
              height: '12px',
              display: 'inline-block',
              margin: '2px 2px'
            }}></div>
            <div style={{ display: 'inline-block', fontSize: '12px' }}>
              Closed Loop - Didn't get Help
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="findHelpTable" style={{ display: 'block' }}>
        {showGrid ? (
          <table
            id="findhelptable"
            style={{
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%',
              lineHeight: '25px',
              margin: '30px'
            }}
          >
            <thead>
              <tr style={{ backgroundColor: '#d3d3d5', fontSize: '13px' }}>
                <th width="15%">CCA referral ID</th>
                <th width="15%">Member Name</th>
                <th width="15%">Member ID</th>
                <th width="15%">Medicare</th>
                <th width="15%">Medicaid</th>
                <th width="15%">Staff Name</th>
                <th width="15%">Referral Date</th>
                <th width="15%">Referral Status</th>
                <th width="15%">Referral LastUpdated</th>
                <th width="15%">Provider Name</th>
                <th width="15%">Program Name</th>
                <th width="15%">Category</th>
                <th width="15%">Days SinceReferral</th>
              </tr>
            </thead>
            <tbody>
              {gridData.map((item, index) => (
                <tr key={index}>
                  <td>{item.ccaReferralId}</td>
                  <td>{item.memberName}</td>
                  <td>{item.memberId}</td>
                  <td>{item.medicare}</td>
                  <td>{item.medicaid}</td>
                  <td>{item.staffName}</td>
                  <td>{formatDate(item.referralDate)}</td>
                  <td>{item.referralStatus}</td>
                  <td>{formatDate(item.referralLastUpdated)}</td>
                  <td>{item.providerName}</td>
                  <td>{item.programName}</td>
                  <td>{item.category}</td>
                  <td>{item.daysSinceReferral}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: 'center', margin: '20px' }}>
            <p>Click on chart bars to view detailed referral information</p>
          </div>
        )}
      </div>

      <style jsx>{`
        #findhelptable table, 
        #findhelptable th, 
        #findhelptable td {
          font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
          border: 1px solid #ceced2;
          text-align: center;
        }

        #findhelptable table tr, 
        #findhelptable td {
          font-size: 13px;
          border: 1px solid #dedee0;
          word-wrap: normal;
        }

        #findhelptable table tr:nth-child(odd) {
          background-color: #f9f9f9;
        }

        #findhelptable tr:nth-child(even) {
          background-color: #eee;
        }

        #findhelptable tr:hover td {
          background-color: #b1b1b1;
          cursor: auto;
        }
      `}</style>
    </div>
  );
};

export default FindHelpChart;
